import React, { useEffect, useState } from "react";
import { createIcons, icons } from "lucide";
import { initFlowbite } from "flowbite";
import { NavLink } from "react-router-dom";

export default function Sidebar() {
  const [activePath, setActivePath] = useState(window.location.pathname);

  useEffect(() => {
    // Initialize Lucide icons and Flowbite
    createIcons({ icons });
    initFlowbite();

    // Handle collapse/accordion toggle
    const handleCollapse = (e) => {
      e.preventDefault();
      const element = e.currentTarget;
      const targetId = element.getAttribute("data-collapse-toggle");
      const target = targetId ? document.getElementById(targetId) : null;

      if (target) {
        target.classList.toggle("hidden");
        const icon = element.querySelector("i.icofont-thin-down");
        if (icon) {
          icon.classList.toggle("rotate-180");
        }
      }
    };

    // Attach collapse event listeners
    const collapseElements = document.querySelectorAll(
      "[data-collapse-toggle]"
    );
    collapseElements.forEach((element) => {
      element.addEventListener("click", handleCollapse);
    });

    // Highlight active link
    document.querySelectorAll("ul.navbar-nav a.nav-link").forEach((element) => {
      const href = element.getAttribute("href");
      if (href && activePath.includes(href)) {
        element.classList.add("active");
        const parentCollapse = element.closest('[data-fc-type="collapse"]');
        if (parentCollapse) {
          const parentTargetId = parentCollapse.getAttribute(
            "data-collapse-toggle"
          );
          const parentTarget = parentTargetId
            ? document.getElementById(parentTargetId)
            : null;
          if (parentTarget) {
            parentTarget.classList.remove("hidden");
          }
        }
      }
    });

    // Cleanup event listeners
    return () => {
      collapseElements.forEach((element) => {
        element.removeEventListener("click", handleCollapse);
      });
    };
  }, [activePath]);

  return (
    <div className="min-h-full z-[99] fixed inset-y-0 print:hidden bg-gradient-to-t from-[#6f3dc3] from-10% via-[#603dc3] via-40% to-[#5c3dc3] to-100% dark:bg-[#603dc3] main-sidebar duration-300 group-data-[sidebar=dark]:bg-[#603dc3] group-data-[sidebar=brand]:bg-brand group-[.dark]:group-data-[sidebar=brand]:bg-[#603dc3]">
      <div className="text-center border-b bg-[#603dc3] border-r h-[64px] flex justify-center items-center brand-logo dark:bg-[#603dc3] dark:border-slate-700/40 group-data-[sidebar=dark]:bg-[#603dc3] group-data-[sidebar=dark]:border-slate-700/40 group-data-[sidebar=brand]:bg-brand group-[.dark]:group-data-[sidebar=brand]:bg-[#603dc3] group-data-[sidebar=brand]:border-slate-700/40">
        <NavLink to="/" className="logo">
          <span>
            <img
              src="/images/logo-white.png"
              alt="logo-large"
              className="logo-lg h-[60px] logo-light hidden dark:inline-block ms-1 group-data-[sidebar=dark]:inline-block group-data-[sidebar=brand]:inline-block"
            />
            <img
              src="/images/logo-white.png"
              alt="logo-large"
              className="logo-lg h-[60px] logo-dark inline-block dark:hidden ms-1 group-data-[sidebar=dark]:hidden group-data-[sidebar=brand]:hidden"
            />
          </span>
        </NavLink>
      </div>
      <div
        className="border-r pb-14 h-[100vh] dark:bg-[#603dc3] dark:border-slate-700/40 group-data-[sidebar=dark]:border-slate-700/40 group-data-[sidebar=brand]:border-slate-700/40 simplebar-scrollable-y"
        data-simplebar=""
      >
        <div className="p-4 block">
          <ul className="navbar-nav">
            <li className="uppercase text-[11px] text-primary-500 dark:text-primary-400 mt-0 leading-4 mb-2 group-data-[sidebar=dark]:text-primary-400 group-data-[sidebar=brand]:text-primary-300">
              <span className="text-[9px] text-slate-600 dark:text-slate-500 group-data-[sidebar=dark]:text-slate-500 group-data-[sidebar=brand]:text-slate-400">
                DashboardS &amp; Apps
              </span>
            </li>
            <li>
              <div id="parent-accordion" data-fc-type="accordion">
                {/* Admin Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="Admin-d-flush"
                >
                  <span
                    data-lucide="home"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>Admin</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="Admin-d-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/contact"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Contact List
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/seo-settings"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        SEO Settings
                      </NavLink>
                    </li>
                  </ul>
                </div>
                {/* Blog Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="Blog-flush"
                >
                  <span
                    data-lucide="file-text"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>Blog</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="Blog-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/addblog"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add Blog
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/listblogs"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        List Blogs
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/addblog-category"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Blog Category
                      </NavLink>
                    </li>
                  </ul>
                </div>
                {/* FAQ Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="FAQ-flush"
                >
                  <span
                    data-lucide="help-circle"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>FAQ</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="FAQ-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/AddFaq"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add FAQ
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/listfaq"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        FAQ List
                      </NavLink>
                    </li>
                  </ul>
                </div>
                {/* Event Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="Event-flush"
                >
                  <span
                    data-lucide="calendar"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>Event</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="Event-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/add-event"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add Event
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/event-list"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Event List
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/addevent-category"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Event Category
                      </NavLink>
                    </li>
                  </ul>
                </div>
                {/* Tour Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="Tour-flush"
                >
                  <span
                    data-lucide="map"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>Tour</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="Tour-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/add-tour"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add Tour
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/list-tours"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Tour List
                      </NavLink>
                    </li>
                  </ul>
                </div>
                {/* Location Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="Location-flush"
                >
                  <span
                    data-lucide="globe"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>Location</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="Location-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/add-country"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add Country
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/add-state"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add State
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/add-gallery"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add Gallery
                      </NavLink>
                    </li>
                  </ul>
                </div>
                {/* Destination Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="Destination-flush"
                >
                  <span
                    data-lucide="map-pin"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>Destination</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="Destination-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/add-destination"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add Destination
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/destination-list"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Destination List
                      </NavLink>
                    </li>
                  </ul>
                </div>

                {/* Tour Guide Members Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="Tour-Guide-flush"
                >
                  <span
                    data-lucide="map-pin"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>Tour Guide Members</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="Tour-Guide-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/add-team-members"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add Tour Guide Members
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/list-team-members"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Tour Guide Members List
                      </NavLink>
                    </li>
                  </ul>
                </div>





                   {/* Tour Guide Members Start */}
                <a
                  href="#"
                  className="nav-link hover:bg-transparent hover:text-black rounded-md dark:hover:text-slate-200 flex items-center decoration-0 px-3 py-3 cursor-pointer group-data-[sidebar=dark]:hover:text-slate-200 group-data-[sidebar=brand]:hover:text-slate-200"
                  data-collapse-toggle="Trackind-Guide-flush"
                >
                  <span
                    data-lucide="map-pin"
                    className="w-5 h-5 text-center text-slate-800 dark:text-slate-400 me-2 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400"
                  />
                  <span>Trackings</span>
                  <i className="icofont-thin-down ms-auto inline-block text-[14px] transform transition-transform duration-300 text-slate-800 dark:text-slate-400 group-data-[sidebar=dark]:text-slate-400 group-data-[sidebar=brand]:text-slate-400" />
                </a>
                <div id="Trackind-Guide-flush" className="overflow-hidden hidden">
                  <ul className="nav flex-col flex flex-wrap ps-0 mb-0 ms-2">
                    <li className="nav-item relative block">
                      <NavLink
                        to="/add-tracking"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Add Tracking
                      </NavLink>
                    </li>
                    <li className="nav-item relative block">
                      <NavLink
                        to="/tracking-list"
                        className="nav-link hover:text-primary-500 rounded-md dark:hover:text-primary-500 relative flex items-center decoration-0 px-3 py-3 group-data-[sidebar=brand]:hover:text-slate-200"
                      >
                        <i className="icofont-dotted-right me-2 text-slate-600 text-[8px] group-data-[sidebar=brand]:text-slate-400" />
                        Trackings List
                      </NavLink>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
          </ul>
          <div className="rounded-md py-4 px-3 mt-12 mb-4 relative bg-primary-300/10 text-center">
            <a
              href="javascript: void(0);"
              className="float-right close-btn text-slate-400"
            >
              <i className="mdi mdi-close" />
            </a>
            <h5 className="my-3 text-lg font-medium text-slate-700 dark:text-slate-300 group-data-[sidebar=dark]:text-slate-300 group-data-[sidebar=brand]:text-slate-300">
              GrowShadow Themes
            </h5>
            <p className="mb-3 text-sm text-slate-400">
              We Design and Develop Clean and High Quality Web Applications
            </p>
            <button className="px-2 py-1 mb-2 text-orange-400 hover:text-white border border-orange-300 hover:bg-orange-300 focus:outline-none rounded text-sm text-center dark:border-orange-300 dark:text-orange-300 dark:hover:text-white dark:hover:bg-orange-300">
              Upgrade your plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
